import { type Player, PlayerStatus } from '@infiltre/shared';
import { useMemo } from 'react';
import { useGameStore, useSessionStore } from '@/app/stores';
import {
  canAdvanceFromReveal,
  canCastVote,
  canClochardGuess,
  canEliminate,
  canOpenVote,
  canPromoteHost,
  canRequestNextRound,
  canStart,
  canUpdateOptions,
} from './can-act';
import { findPlayerById, selectRoom } from './selectors';

/**
 * Cross-store helpers. `room` lives in the gameStore but `identity` lives
 * in the sessionStore — these hooks subscribe to both and combine. They
 * mirror what selectMyPlayer/selectAmIHost/selectAmISpectator did before
 * the store split, with the same memoization guarantees.
 */

const useIdentityPlayerId = (): string | null =>
  useSessionStore((s) => s.identity?.playerId ?? null);

export const useMyPlayer = (): Player | null => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return useMemo(() => findPlayerById(room, playerId), [room, playerId]);
};

export const useAmIHost = (): boolean => useMyPlayer()?.isHost ?? false;

export const useAmISpectator = (): boolean => {
  const me = useMyPlayer();
  return me?.status === PlayerStatus.Eliminated;
};

/**
 * Action-gate hooks. Each returns `true` iff the engine would currently
 * accept the action from the local identity. Pair with disabled state on
 * the corresponding CTA so stale clicks never reach the server.
 */
export const useCanStart = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canStart(room, playerId);
};

export const useCanOpenVote = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canOpenVote(room, playerId);
};

export const useCanCastVote = (targetId: string): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canCastVote(room, playerId, targetId);
};

export const useCanEliminate = (targetId: string): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canEliminate(room, playerId, targetId);
};

export const useCanClochardGuess = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canClochardGuess(room, playerId);
};

export const useCanAdvanceFromReveal = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canAdvanceFromReveal(room, playerId);
};

export const useCanRequestNextRound = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canRequestNextRound(room, playerId);
};

export const useCanPromoteHost = (targetId: string): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canPromoteHost(room, playerId, targetId);
};

export const useCanUpdateOptions = (): boolean => {
  const room = useGameStore(selectRoom);
  const playerId = useIdentityPlayerId();
  return canUpdateOptions(room, playerId);
};
